import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Subnav } from '@air/api';
import { type SortDirection, type Subnav as SubnavType } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isBoardSubBoardsKey } from '~/swr-hooks/boards/useSubBoardsList';
import { getWorkspaceSubnavSortKey } from '~/swr-hooks/subnav/useSubnavSort';

type UpdateSubnavSortParams = {
  name: string;
  direction: SortDirection;
  libraryId?: string;
};

export const useUpdateSubnavSort = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, direction, libraryId }: UpdateSubnavSortParams) => {
      if (!currentWorkspace?.id) {
        throw new Error('No current workspace');
      }

      if (libraryId) {
        return Subnav.updateSubnav({
          workspaceId: currentWorkspace.id,
          update: { libraryBoardSorts: [{ name, direction, libraryId }] },
        });
      }

      return Subnav.updateSubnav({
        workspaceId: currentWorkspace.id,
        update: { boardSort: { name, direction } },
      });
    },

    onMutate: async ({ name, direction, libraryId }: UpdateSubnavSortParams) => {
      if (!currentWorkspace?.id) {
        return;
      }

      const key = getWorkspaceSubnavSortKey(currentWorkspace.id);
      await queryClient.cancelQueries({ queryKey: key });
      const previousSubnavSort = queryClient.getQueryData<SubnavType>(key);

      if (libraryId) {
        queryClient.setQueryData<SubnavType>(key, (oldData) => {
          const libraryBoardSorts = oldData?.libraryBoardSorts ?? [];
          const updatedLibraryBoardSorts = libraryBoardSorts.filter((sort) => sort.libraryId !== libraryId);
          updatedLibraryBoardSorts.push({ name, direction, libraryId });

          return {
            ...oldData,
            workspaceId: currentWorkspace.id,
            libraryBoardSorts: updatedLibraryBoardSorts,
          };
        });
      } else {
        queryClient.setQueryData<SubnavType>(key, (oldData) => ({
          ...oldData,
          workspaceId: currentWorkspace.id,
          boardSort: { name, direction },
        }));
      }

      return { previousSubnavSort, key };
    },

    onError: (error, { name, direction, libraryId }, context) => {
      if (context?.previousSubnavSort && context?.key) {
        queryClient.setQueryData(context.key, context.previousSubnavSort);
      }

      if (libraryId) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to update subnav library board sort',
          metadata: { data: { libraryBoardSort: { name, direction, libraryId } } },
        });
      } else {
        reportErrorToBugsnag({
          error,
          context: 'Failed to update subnav board sort',
          metadata: { data: { boardSort: { name, direction } } },
        });
      }
    },

    onSettled: (_data, _error, { libraryId }) => {
      if (currentWorkspace?.id) {
        const key = getWorkspaceSubnavSortKey(currentWorkspace.id);
        queryClient.invalidateQueries({ queryKey: key });

        // Invalidate sub-boards queries since they now respect library sorts
        // When libraryId is provided, only invalidate sub-boards within that library
        // When libraryId is not provided, invalidate all workspace sub-boards
        queryClient.invalidateQueries({
          predicate: (query) => isBoardSubBoardsKey(query.queryKey, libraryId),
        });
      }
    },
  });
};
